import { actions } from "@mixitone/actions";
import { mixitoneImagesPublicUrl } from "@mixitone/util";
import tauri from "lib/tauri";

function normalizeContentType(contentType: string): string {
  return contentType.trim().split(";")[0].trim().toLowerCase();
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Uploads a PDF to the private documents bucket. Returns the file id stored on the document row. */
export async function uploadClubDocumentObject(options: {
  accountId: string;
  clubId: string;
  file: File;
}): Promise<string> {
  const contentType = normalizeContentType(options.file.type || "application/pdf");
  if (contentType !== "application/pdf") {
    throw new Error("Unsupported file type");
  }

  const fileId = crypto.randomUUID();
  const body = await options.file.arrayBuffer();

  if (tauri.enabled) {
    await tauri.invoke("plugin:session|upload_mixitone_image", {
      accountId: options.accountId,
      clubId: options.clubId,
      relativePath: fileId,
      contentType,
      dataBase64: arrayBufferToBase64(body),
    });
    return fileId;
  }

  const data = await actions.documents.presignDocumentUpload({
    accountId: options.accountId,
    clubId: options.clubId,
    fileId,
    contentType,
  });

  const put = await fetch(data.putUrl, {
    method: "PUT",
    body,
    headers: { "Content-Type": data.contentType },
  });
  if (!put.ok) {
    const detail = (await put.text()).trim();
    const suffix = detail ? `: ${detail.slice(0, 800)}` : "";
    throw new Error(`Upload failed: ${put.status}${suffix}`);
  }
  return fileId;
}

/** Public images URL for older files, or a short-lived signed URL for private files. */
export async function clubDocumentReadUrl(options: {
  fileStorage?: string | null;
  accountId: string;
  fileId: string;
  clubDocumentId?: string | null;
  clubId?: string | null;
}): Promise<string> {
  if (options.fileStorage !== "private_documents") {
    return mixitoneImagesPublicUrl(`${options.accountId}/documents/${options.fileId}`);
  }
  if (options.clubDocumentId) {
    const signed = await actions.documents.signDocumentRead({ clubDocumentId: options.clubDocumentId });
    return signed.url;
  }
  if (options.clubId) {
    const signed = await actions.documents.signDocumentRead({
      clubId: options.clubId,
      fileId: options.fileId,
    });
    return signed.url;
  }
  throw new Error("Document reference required");
}
